/**
 * Card #280 round 2 — the Auto Approval lead time / cutoff window, as the
 * order form's date pickers see it.
 *
 * For Pickup with Layover, Dropoff with Layover and Same Day orders:
 *
 *   earliest pickup   = today + lead days (orders.autoApproval.leadDays.<type>),
 *                       plus one more day once the type's cutoff hour
 *                       (orders.autoApproval.cutoff.<type>) has passed on the
 *                       `tz` setting's wall clock;
 *   earliest delivery = earliest pickup + 1 day (hardcoded; Same Day: + 0,
 *                       see below). The existing route-day greying then
 *                       leaves the destination's next route day as the first
 *                       one offered.
 *
 * Lead 2, 10 AM cutoff, Tue Oct 6: before 10 AM pickup >= Thu Oct 8 and
 * delivery >= Fri Oct 9; after 10 AM pickup >= Fri Oct 9 and delivery >=
 * Sat Oct 10 (so the next route day, e.g. Mon Oct 12).
 *
 * Same Day orders have no pickup field: they are picked up and delivered on
 * the delivery day (round 3), so for them the earliest delivery IS the
 * earliest pickup, not the day after it. With no lead days a customer can
 * order Same Day for today until the Same Day cutoff hour; Same Day lead days
 * = 1 gives the old "order the day before" rule. Only the delivery limit
 * shows.
 *
 * The window exists only while Auto-Approve New Orders AND that order type's
 * auto-approve switch are on. Each type has its own Lead Time Bypass
 * (orders.autoApproval.<type>Bypass: sameDayBypass, layoverBypass,
 * dropoffBypass; round 4; each default off). It stops that type's cutoff hour
 * from greying anything; it never changes what auto-approval accepts, which is
 * why `earliestPickup` (what the server auto-approves) and `offeredPickup`
 * (what customers may pick) are separate.
 *
 * The server's orderDateCheck (server/src/controllers/orders/utils.js)
 * applies the same earliest-pickup formula. Everything marked LOCKSTEP is
 * duplicated there and MUST stay identical: settingsConfig.json's
 * defaultValue never reaches either side, so each setting's effective default
 * is the fallback at each read site.
 *
 * PURE module (same contract as ./holidays.js): no imports, no I/O, no moment,
 * no lodash. Config and the clock are passed in, so this can be required
 * straight out of node:
 *
 *   node -e "const w=require('./app/src/actions/autoapprovaldates');
 *            console.log(w.autoApprovalWindow('layover',
 *              k => ({ 'orders.autoApprove': true })[k],
 *              new Date('2026-10-06T15:00:00Z')));"
 *
 * Every day in and out is a bare 'YYYY-MM-DD' calendar day string.
 */

// LOCKSTEP with server/src/controllers/orders/utils.js — DEFAULT_TZ,
// AUTO_APPROVAL_CUTOFF_DEFAULTS, AUTO_APPROVAL_LEAD_DAY_DEFAULTS,
// AUTO_APPROVAL_TYPE_DEFAULTS, AUTO_APPROVAL_BYPASS_DEFAULT (= the
// LEAD_TIME_BYPASS_DEFAULT below) with the same per-type key, and the
// orders.autoApprove fallback (false) in server/src/controllers/orders/index.js.
const DEFAULT_TZ = 'America/Denver';
const AUTO_APPROVAL_CUTOFF_DEFAULTS = { layover: 10, dropoff: 14, sameDay: 14 };
const AUTO_APPROVAL_LEAD_DAY_DEFAULTS = { layover: 0, dropoff: 0, sameDay: 0 };
const AUTO_APPROVAL_TYPE_DEFAULTS = { layover: true, dropoff: true, sameDay: true };
const LEAD_TIME_BYPASS_DEFAULT = false;
// One Lead Time Bypass per order type: orders.autoApproval.sameDayBypass (the
// round-2 key, now Same Day only), .layoverBypass and .dropoffBypass.
const leadTimeBypassKey = orderType => `orders.autoApproval.${orderType}Bypass`;

const WINDOW_ORDER_TYPES = ['layover', 'dropoff', 'sameDay'];

// Same coercion as the server's numSetting / boolSetting: a setting saved as
// null (or any other wrong kind) falls back instead of winning.
const numSetting = (v, fb) => (typeof v === 'number' && isFinite(v) ? v : fb);
const boolSetting = (v, fb) => (typeof v === 'boolean' ? v : fb);

const pad = n => `${n}`.padStart(2, '0');
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

// The calendar day a date value means, as 'YYYY-MM-DD'.
//   - a moment (every value the order form holds, noon-normalised by
//     getMidDate) or a Date: its LOCAL calendar day, i.e. the day the user
//     clicked;
//   - an ISO string: its date part as written.
// For any US timezone these agree with the UTC date part the server reads off
// the noon-local instant the form submits.
const calendarDay = (d) => {
  if (!d) { return null; }
  if (typeof d === 'string') {
    const m = DAY_RE.exec(d);
    return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
  }
  if (typeof d.format === 'function') {
    if (typeof d.isValid === 'function' && !d.isValid()) { return null; }
    return d.format('YYYY-MM-DD');
  }
  if (d instanceof Date && !isNaN(d.getTime())) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
  return null;
};

// Integer day arithmetic on 'YYYY-MM-DD' strings. Date.UTC is used purely as
// fixed-offset arithmetic, so DST and the host timezone never enter into it.
const addDays = (day, n) => {
  const [y, m, d] = day.split('-').map(Number);
  const out = new Date(Date.UTC(y, m - 1, d + n));
  return `${out.getUTCFullYear()}-${pad(out.getUTCMonth() + 1)}-${pad(out.getUTCDate())}`;
};

// Wall clock in the operating timezone — the browser twin of the server's
// moment().tz(tz). app/ has no moment-timezone, so this reads it through Intl.
const FORMATTERS = new Map();
const wallClock = (tz, now) => {
  if (!FORMATTERS.has(tz)) {
    // Throws RangeError for an unknown zone; the caller falls back.
    FORMATTERS.set(tz, new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }));
  }
  const parts = {};
  FORMATTERS.get(tz).formatToParts(now).forEach(p => { parts[p.type] = p.value; });
  return {
    today: `${parts.year}-${parts.month}-${parts.day}`,
    // Same formula as the server: hour + minute / 60. `% 24` guards engines
    // that still print midnight as "24" under hourCycle h23.
    nowHour: (Number(parts.hour) % 24) + (Number(parts.minute) / 60),
  };
};

/**
 * The window for one order type, or null when there is none: auto-approve
 * off, that type's switch off, or a type auto-approval never date-checks
 * (returns, payment pickups, ...).
 *
 * `getConfig` is the app's getConfig(key, fallback); `now` is injectable for
 * testing.
 *
 *   earliestPickup / earliestDelivery — what auto-approval accepts (the
 *     cutoff always counts; LOCKSTEP with orderDateCheck's earliestPickup).
 *   offeredPickup / offeredDelivery   — the first days customers may pick
 *     (the cutoff counts only while the bypass is off).
 */
const autoApprovalWindow = (orderType, getConfig, now = new Date()) => {
  if (!WINDOW_ORDER_TYPES.includes(orderType)) { return null; }
  const read = (key, fb) => (typeof getConfig === 'function' ? getConfig(key, fb) : fb);

  // Same truthiness test as /api/orders/create.
  if (!read('orders.autoApprove', false)) { return null; }
  const typeFb = AUTO_APPROVAL_TYPE_DEFAULTS[orderType];
  if (!boolSetting(read(`orders.autoApproval.types.${orderType}`, typeFb), typeFb)) { return null; }

  // Same key, fallback and `|| DEFAULT_TZ` as the server's orderDateCheck.
  const tz = read('tz', DEFAULT_TZ) || DEFAULT_TZ;
  let clock;
  try {
    clock = wallClock(tz, now);
  } catch (err) {
    clock = wallClock(DEFAULT_TZ, now);
  }

  const cutoffFb = AUTO_APPROVAL_CUTOFF_DEFAULTS[orderType];
  const cutoffHour = numSetting(read(`orders.autoApproval.cutoff.${orderType}`, cutoffFb), cutoffFb);
  const leadFb = AUTO_APPROVAL_LEAD_DAY_DEFAULTS[orderType];
  const leadDays = numSetting(read(`orders.autoApproval.leadDays.${orderType}`, leadFb), leadFb);
  const bypass = boolSetting(read(leadTimeBypassKey(orderType), LEAD_TIME_BYPASS_DEFAULT), LEAD_TIME_BYPASS_DEFAULT);

  // LOCKSTEP with orderDateCheck: whole days, rounded up; 0 or less = none.
  const leadWhole = leadDays > 0 ? Math.ceil(leadDays) : 0;
  const pastCutoff = clock.nowHour > cutoffHour;

  const earliestPickup = addDays(clock.today, leadWhole + (pastCutoff ? 1 : 0));
  const offeredPickup = addDays(clock.today, leadWhole + (pastCutoff && !bypass ? 1 : 0));
  // Delivery is the day after pickup, except Same Day: picked up and
  // delivered the same day (LOCKSTEP with orderDateCheck, which counts a Same
  // Day order's lead days and cutoff to its delivery day).
  const deliveryGap = orderType === 'sameDay' ? 0 : 1;

  return {
    orderType,
    today: clock.today,
    nowHour: clock.nowHour,
    cutoffHour,
    leadDays: leadWhole,
    pastCutoff,
    bypass,
    earliestPickup,
    earliestDelivery: addDays(earliestPickup, deliveryGap),
    offeredPickup,
    offeredDelivery: addDays(offeredPickup, deliveryGap),
  };
};

// Is `date` (a moment, Date or ISO string) before the day `earliest`?
const isBeforeDay = (date, earliest) => {
  const day = calendarDay(date);
  return !!(day && earliest && day < earliest);
};

module.exports = {
  DEFAULT_TZ,
  AUTO_APPROVAL_CUTOFF_DEFAULTS,
  AUTO_APPROVAL_LEAD_DAY_DEFAULTS,
  AUTO_APPROVAL_TYPE_DEFAULTS,
  LEAD_TIME_BYPASS_DEFAULT,
  leadTimeBypassKey,
  WINDOW_ORDER_TYPES,
  calendarDay,
  addDays,
  autoApprovalWindow,
  isBeforeDay,
};
